'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';

import {
    Alert,
    Divider,
    Spacer,
    SpacerSize,
    StatusState,
    Text,
    Title,
    TitleLevels,
    TitleMarkup,
} from '@bytel/trilogy-react';

import { getParentalOptions } from '@actions/get-parental-options';

import { OptionType, SubscribedOption } from '@app-types/product';
import { TagEventType } from '@app-types/tracking';

import { BoxInfo } from '@components/box-info';
import { InlinePrice } from '@components/inline-price';
import { ListItemWithPrice } from '@components/list-item-with-price';
import { OptionsList } from '@components/options-list';
import PlanPlusOptionHeader from '@components/plan-plus-option-header';

import { useCart } from '@hooks/use-cart';
import useTracking from '@hooks/use-tracking';

import { paths } from '@helpers/path';
import { isPack } from '@helpers/product';

import InflationModal from './inflation-modal';
import { ParentalControl } from './parental-control';

type CartMonthlyProps = {
    total: number;
    showInflationAlert: boolean;
};

export default function CartMonthly({ total, showInflationAlert }: Readonly<CartMonthlyProps>) {
    const { byouCart, updateCartOptions } = useCart();
    const [shouldShowInflationModal, setShouldShowInflationModal] = useState(false);
    const { sendTag } = useTracking();
    const [parentalOptions, setParentalOptions] = useState<OptionType[]>([]);
    const [subscribedOptions, setSubscribedOptions] = useState<SubscribedOption[]>([]);

    useEffect(() => {
        async function fetchParentalOptions() {
            if (!byouCart?.gencode) {
                setParentalOptions([]);
                setSubscribedOptions([]);
                return;
            }
            const { options, subscribedOptions: loadedSubscribedOptions } = await getParentalOptions(byouCart.gencode);
            setParentalOptions(options);
            setSubscribedOptions(loadedSubscribedOptions);
        }
        fetchParentalOptions();
    }, [byouCart?.gencode]);

    if (!byouCart) {
        return null;
    }

    const { product, options } = byouCart;

    if (!product) {
        return null;
    }

    const handleShowInflationModal = () => {
        setShouldShowInflationModal(true);
        sendTag(
            {
                actionClic: 'Clic_selection',
                categorieClic: 'AB_test_transparence_prix',
                nomClic: 'En_savoir_plus',
            },
            TagEventType.CLICK,
        );
    };

    const onCloseInflationModal = () => {
        setShouldShowInflationModal(false);
        sendTag(
            {
                actionClic: 'Clic_selection',
                categorieClic: 'AB_test_transparence_prix',
                nomClic: 'Fermer',
            },
            TagEventType.CLICK,
        );
    };

    return (
        <>
            <BoxInfo header={<PlanPlusOptionHeader total={total} />}>
                <Title level={TitleLevels.FIVE} markup={TitleMarkup.H3}>
                    Votre forfait sans engagement
                </Title>
                <ListItemWithPrice
                    title={`${product?.name} par mois`}
                    amount={product.prices.final}
                    period='mois'
                    titleClassName='!has-text-weight-normal'
                />
                {product.prices.final !== product.prices.forever ? (
                    <>
                        <Spacer size={SpacerSize.TWO} />
                        <Text>
                            Pendant {product.prices.duration} mois puis{' '}
                            <InlinePrice amount={product.prices.forever} period='mois' />
                        </Text>
                    </>
                ) : null}
                <Spacer size={SpacerSize.TWO} />
                <Link
                    className='link'
                    href={isPack(product) ? paths.packs : paths.plans}
                    onClick={() => {
                        sendTag(
                            {
                                actionClic: 'Clic_selection',
                                categorieClic: 'Panier',
                                nomClic: 'Modification_forfait',
                            },
                            TagEventType.CLICK,
                        );
                    }}
                >
                    Modifier mon forfait
                </Link>
                {showInflationAlert ? (
                    <Alert
                        status={StatusState.INFO}
                        display
                        description={
                            <>
                                Ce prix restera le même les 15 premiers mois. Il sera ensuite indexé annuellement.
                                <br />
                                <button onClick={handleShowInflationModal} className='button-clear link'>
                                    Vous voulez en savoir plus ?
                                </button>
                            </>
                        }
                    />
                ) : null}
                <Divider />
                {parentalOptions?.length > 0 && (
                    <ParentalControl
                        parentalOptions={parentalOptions}
                        cartOptions={options}
                        subscribedOptions={subscribedOptions}
                    />
                )}
                <OptionsList updatable cart={byouCart} updateCartOptions={updateCartOptions} />
            </BoxInfo>
            <InflationModal active={shouldShowInflationModal} onClose={onCloseInflationModal} />
        </>
    );
}
